import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { useLang } from "@/i18n";
import { waLink } from "@/data/site";
import { WhatsAppIcon } from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-all duration-200 placeholder:text-ink-muted/60 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

export const BookingForm = () => {
  const { t, lang } = useLang();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", check_in: "", check_out: "",
    guests: 2, pets: false, message: "",
  });

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/availability-requests`, { ...form, guests: Number(form.guests), lang });
      toast.success(t.form.success);
      setForm({ name: "", email: "", phone: "", check_in: "", check_out: "", guests: 2, pets: false, message: "" });
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setSending(false);
    }
  };

  const waHref = () => {
    const parts = [t.form.waMsg];
    if (form.check_in && form.check_out) parts.push(`${form.check_in} → ${form.check_out}`);
    parts.push(`${t.form.guests}: ${form.guests}`);
    if (form.pets) parts.push(lang === "it" ? "con animali" : "with pets");
    if (form.name) parts.push(`— ${form.name}`);
    return waLink(parts.join(", "));
  };

  return (
    <form data-testid="booking-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      <div>
        <label htmlFor="bf-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.name} *</label>
        <input id="bf-name" data-testid="booking-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="bf-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.email} *</label>
        <input id="bf-email" data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="bf-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.phone}</label>
        <input id="bf-phone" data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="bf-guests" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.guests} *</label>
        <select id="bf-guests" data-testid="booking-guests-select" value={form.guests} onChange={set("guests")} className={inputCls}>
          {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div>
        <label htmlFor="bf-checkin" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.checkin} *</label>
        <input id="bf-checkin" data-testid="booking-checkin-input" type="date" required value={form.check_in} onChange={set("check_in")} className={inputCls} />
      </div>
      <div>
        <label htmlFor="bf-checkout" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.checkout} *</label>
        <input id="bf-checkout" data-testid="booking-checkout-input" type="date" required min={form.check_in} value={form.check_out} onChange={set("check_out")} className={inputCls} />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor="bf-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-ink-muted">{t.form.message}</label>
        <textarea id="bf-message" data-testid="booking-message-input" rows={4} placeholder={t.form.messagePh} value={form.message} onChange={set("message")} className={inputCls} />
      </div>
      <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink sm:col-span-2" data-testid="booking-pets-toggle">
        <input type="checkbox" checked={form.pets} onChange={set("pets")} className="h-5 w-5 rounded border-border accent-terracotta" />
        {t.form.pets}
      </label>
      <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row">
        <button
          type="submit"
          data-testid="booking-submit-button"
          disabled={sending}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-bold text-sand shadow-lg shadow-terracotta/25 transition-all duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5 disabled:opacity-60"
        >
          {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
          {sending ? t.form.sending : t.form.submit}
        </button>
        <a
          data-testid="booking-whatsapp-button"
          href={waHref()}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-[#25D366] bg-white px-6 py-3.5 text-sm font-bold text-[#128C4B] transition-all duration-200 hover:bg-[#25D366] hover:text-white"
        >
          <WhatsAppIcon size={18} /> {t.form.whatsapp}
        </a>
      </div>
    </form>
  );
};
